import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  CheckCircle2, AlertCircle, AlertTriangle, Loader2, ArrowRight, Phone, Mail,
} from "lucide-react";
import SEOHead from "@/components/SEOHead";

/**
 * Quote-acceptance landing page.
 *
 * URL: /accept-quote — Zoho automations email a single-use ?token=<...> link.
 * React Router matches both /accept-quote and /accept-quote/. The page POSTs
 * the token to /api/accept-quote, which proxies to the Zoho accept_quote
 * Deluge function under OAuth (legacy WordPress used an API key; G1.2 swapped
 * to OAuth so the API-key route can be retired).
 *
 * Success condition mirrors legacy WordPress:
 *   data.code === "success" && data.details.output === "success"
 *
 * Anything else — no token, transport error, non-success body — renders the
 * same generic failure message asking the user to contact us. noindex,nofollow
 * because the page is only reachable from automation emails; the path is
 * intentionally excluded from sitemap-routes.json for the same reason.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

const NEXT_STEPS = [
  "Review your quotation",
  "Sign the application documents",
  "We configure and prepare your setup",
  "Your terminals and onboarding are arranged",
  "You start taking payments with CanDo",
];

function StatusCard({ icon: Icon, iconBg, iconColor, eyebrow, title, children, footer }) {
  return (
    <div className="max-w-2xl mx-auto bg-white border border-brand-light-grey rounded-2xl p-8 sm:p-10 shadow-sm">
      <div className="flex flex-col items-center text-center">
        <div className={`w-16 h-16 rounded-2xl ${iconBg} flex items-center justify-center mb-5`}>
          <Icon size={28} className={iconColor} />
        </div>
        {eyebrow && (
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-grey mb-3">
            {eyebrow}
          </p>
        )}
        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-brand-black tracking-tight mb-4">
          {title}
        </h2>
        <div className="text-base text-brand-grey leading-relaxed">{children}</div>
        {footer && <div className="mt-7 w-full">{footer}</div>}
      </div>
    </div>
  );
}

export default function AcceptQuotePage() {
  const [params] = useSearchParams();
  const token = (params.get("token") || "").trim();
  // States: "invalid" (no token), "loading", "success", "failure".
  const [status, setStatus] = useState(token ? "loading" : "invalid");

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.post(`${API}/accept-quote`, { token });
        const ok =
          data && data.code === "success" && data.details && data.details.output === "success";
        if (!cancelled) setStatus(ok ? "success" : "failure");
      } catch {
        // Any non-2xx (transport error, auth error, missing token rejected
        // server-side) collapses to the same user-visible failure card.
        if (!cancelled) setStatus("failure");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div data-testid="accept-quote-page">
      <SEOHead
        title="Accept Your Quote"
        description="Confirm your CanDo Payments quote."
        path="/accept-quote"
        robots="noindex, nofollow"
      />

      <section className="hero-gradient py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.p
              variants={fadeUp}
              className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua mb-3"
            >
              Quote Confirmation
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="font-heading text-4xl sm:text-5xl font-bold text-brand-black tracking-tight leading-tight mb-5"
            >
              <span className="gradient-text">Accept</span> your quote
            </motion.h1>
            <motion.p variants={fadeUp} className="text-lg text-brand-grey leading-relaxed">
              {status === "loading"
                ? "One moment while we confirm your acceptance."
                : "We’ll take it from here."}
            </motion.p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {status === "loading" && (
            <StatusCard
              icon={Loader2}
              iconBg="bg-brand-aqua-light"
              iconColor="text-brand-aqua animate-spin"
              title="Confirming your acceptance"
              data-testid="accept-quote-loading"
            >
              <p>Just a few seconds &mdash; please don&rsquo;t close this tab.</p>
            </StatusCard>
          )}

          {status === "success" && (
            <div data-testid="accept-quote-success">
              <StatusCard
                icon={CheckCircle2}
                iconBg="bg-emerald-50"
                iconColor="text-emerald-600"
                eyebrow="Accepted"
                title="Thank you!"
                footer={
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Link
                      to="/"
                      className="inline-flex items-center justify-center gap-2 bg-brand-aqua hover:bg-brand-aqua-dark text-white font-semibold rounded-full px-6 py-3 text-sm transition-colors"
                      data-testid="accept-quote-home-cta"
                    >
                      Back to home <ArrowRight size={14} />
                    </Link>
                  </div>
                }
              >
                <p>
                  Your quote has been accepted. A member of our team will be in touch shortly.
                </p>
              </StatusCard>
            </div>
          )}

          {status === "failure" && (
            <div data-testid="accept-quote-failure">
              <StatusCard
                icon={AlertCircle}
                iconBg="bg-amber-50"
                iconColor="text-amber-600"
                eyebrow="Something went wrong"
                title="We couldn’t process your acceptance"
                footer={
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <a
                      href="tel:+448003682989"
                      className="inline-flex items-center justify-center gap-2 bg-brand-aqua hover:bg-brand-aqua-dark text-white font-semibold rounded-full px-6 py-3 text-sm transition-colors"
                      data-testid="accept-quote-phone-cta"
                    >
                      <Phone size={14} /> 0800 368 2989
                    </a>
                    <a
                      href="mailto:hello@CanDoPayments.com"
                      className="inline-flex items-center justify-center gap-2 bg-white border border-brand-light-grey hover:border-brand-aqua text-brand-black font-semibold rounded-full px-6 py-3 text-sm transition-colors"
                      data-testid="accept-quote-email-cta"
                    >
                      <Mail size={14} /> hello@CanDoPayments.com
                    </a>
                  </div>
                }
              >
                <p>
                  Please contact us directly and we&rsquo;ll sort it out straight away.
                </p>
              </StatusCard>
            </div>
          )}

          {status === "invalid" && (
            <div data-testid="accept-quote-invalid">
              <StatusCard
                icon={AlertTriangle}
                iconBg="bg-amber-50"
                iconColor="text-amber-600"
                eyebrow="Invalid Link"
                title="This link doesn’t look right"
                footer={
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <a
                      href="tel:+448003682989"
                      className="inline-flex items-center justify-center gap-2 bg-brand-aqua hover:bg-brand-aqua-dark text-white font-semibold rounded-full px-6 py-3 text-sm transition-colors"
                    >
                      <Phone size={14} /> 0800 368 2989
                    </a>
                    <a
                      href="mailto:hello@CanDoPayments.com"
                      className="inline-flex items-center justify-center gap-2 bg-white border border-brand-light-grey hover:border-brand-aqua text-brand-black font-semibold rounded-full px-6 py-3 text-sm transition-colors"
                    >
                      <Mail size={14} /> hello@CanDoPayments.com
                    </a>
                  </div>
                }
              >
                <p>
                  We couldn&rsquo;t find a quote token in this URL. Please open the link from your
                  acceptance email, or get in touch and we&rsquo;ll resend it.
                </p>
              </StatusCard>
            </div>
          )}
        </div>
      </section>

      {status === "success" && (
        <section className="py-16 md:py-20 bg-gray-50" data-testid="accept-quote-next-steps">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={stagger}
              className="text-center mb-10"
            >
              <motion.p
                variants={fadeUp}
                className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua mb-3"
              >
                Next Steps
              </motion.p>
              <motion.h2
                variants={fadeUp}
                className="font-heading text-3xl sm:text-4xl font-bold text-brand-black tracking-tight"
              >
                What Happens Next
              </motion.h2>
            </motion.div>
            <motion.ol
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={stagger}
              className="space-y-4"
            >
              {NEXT_STEPS.map((label, i) => (
                <motion.li
                  key={label}
                  variants={fadeUp}
                  className="flex items-center gap-4 bg-white border border-brand-light-grey rounded-2xl p-5 sm:p-6 shadow-sm"
                >
                  <div className="w-12 h-12 rounded-xl bg-brand-aqua text-white font-heading text-xl font-bold flex items-center justify-center flex-shrink-0">
                    {i + 1}
                  </div>
                  <p className="text-base sm:text-lg text-brand-black leading-relaxed">{label}</p>
                </motion.li>
              ))}
            </motion.ol>
            <motion.p
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.5 }}
              variants={fadeUp}
              className="text-base text-brand-grey leading-relaxed text-center mt-10"
            >
              Most setups can be completed within just a few working days.
            </motion.p>
          </div>
        </section>
      )}
    </div>
  );
}
